/**
 * Saneado puro del texto que se interpola en un system prompt (issue #95,
 * Capa 1). Vive en Domain porque no depende de nada y lo comparten el servidor
 * (`server/application/prompt-safety.ts`, que lo re-exporta) y el cliente: el
 * alta de perfil pre-rellena el nombre del chat público exactamente como lo
 * sanearía el servidor (AC2.3).
 */
// Topes de longitud para el texto de perfil que se interpola en el system
// prompt (anti prompt-stuffing). Generosos: la defensa principal es estructural
// (colapsar saltos de línea), no la longitud. Un nombre no necesita más de 120;
// una nota de padres, más de 1000.
const MAX_NAME_LENGTH = 120
const MAX_TEXT_LENGTH = 1000

// Rangos de code points peligrosos, definidos numéricamente para no dejar bytes
// de control/invisibles en el fuente (ilegibles y frágiles al editar).
type CodePointRange = readonly [number, number]

// Control C0/C1 + DEL (incluye \n \r \t): se convierten en espacio para dejar el
// valor en una sola línea.
const CONTROL_RANGES: readonly CodePointRange[] = [
  [0x00, 0x1f],
  [0x7f, 0x9f],
]

// Invisibles/ofuscadores que ocultan o reordenan un payload sin verse:
// zero-width y marcas L/R (200B-200F), controles bidi embeddings/overrides
// (202A-202E), word-joiner e invisibles matemáticos (2060-2064), aislantes y
// deprecados (2066-206F) y BOM (FEFF). Se eliminan por completo.
const INVISIBLE_RANGES: readonly CodePointRange[] = [
  [0x200b, 0x200f],
  [0x202a, 0x202e],
  [0x2060, 0x2064],
  [0x2066, 0x206f],
  [0xfeff, 0xfeff],
]

/** Character class regex built from code-point ranges (keeps the source ASCII). */
function charClassFrom(ranges: readonly CodePointRange[]): RegExp {
  const body = ranges
    .map(([lo, hi]) =>
      lo === hi
        ? String.fromCharCode(lo)
        : `${String.fromCharCode(lo)}-${String.fromCharCode(hi)}`
    )
    .join('')
  return new RegExp(`[${body}]`, 'g')
}

const CONTROL_CHARS = charClassFrom(CONTROL_RANGES)
const INVISIBLE_CHARS = charClassFrom(INVISIBLE_RANGES)

/**
 * Neutraliza un campo de texto libre para interpolarlo con seguridad en el
 * system prompt del LLM (issue #95, Capa 1). El prompt es markdown estructurado
 * por saltos de línea y secciones (`##`, `---`, viñetas `-`); el vector de
 * inyección es "salir" del hueco de dato y abrir una sección o instrucción
 * falsa. Defensa estructural: colapsa TODO whitespace (incluidos los saltos de
 * línea) a un único espacio, elimina caracteres de control e invisibles, y
 * acota la longitud. El resultado sigue siendo texto legible, así que es seguro
 * también para logs y UI. Idempotente.
 */
export function sanitizePromptField(value: string, maxLength: number = MAX_TEXT_LENGTH): string {
  const collapsed = value
    .replace(CONTROL_CHARS, ' ')
    .replace(INVISIBLE_CHARS, '')
    .replace(/\s+/g, ' ')
    .trim()
  return collapsed.length > maxLength ? collapsed.slice(0, maxLength).trim() : collapsed
}

/** El nombre del niño tal y como entra al prompt (whitespace colapsado, sin invisibles, acotado). */
export function toPromptSafeName(name: string): string {
  return sanitizePromptField(name, MAX_NAME_LENGTH)
}
